import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Package, Pencil, Plus, Trash2 } from 'lucide-react';

import { errorMessage } from '@/api/client';
import { productsApi } from '@/api/resources';
import {
  EmptyState,
  Loading,
  Money,
  MoneyInput,
  PageHeader,
  StatCard,
  SubmitButton,
} from '@/components/common';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatGs } from '@/lib/currency';

const EMPTY = {
  name: '',
  sku: '',
  description: '',
  supplier: '',
  category: '',
  cost: 0,
  price: 0,
  stock: 0,
  stock_min: 0,
};

const isLow = (p) => p.stock <= p.stock_min;

export default function Stock() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyLow, setOnlyLow] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [adjusting, setAdjusting] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');

  const load = useCallback(async () => {
    try {
      setProducts(await productsApi.list());
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudieron cargar los productos'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({
      name: product.name,
      sku: product.sku,
      description: product.description || '',
      supplier: product.supplier || '',
      category: product.category || '',
      cost: product.cost,
      price: product.price,
      stock: product.stock,
      stock_min: product.stock_min,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.price < form.cost) {
      const proceed = window.confirm(
        `El precio (${formatGs(form.price)}) es menor al costo (${formatGs(form.cost)}). ` +
          `Vas a perder plata en cada venta. ¿Guardar igual?`
      );
      if (!proceed) return;
    }

    setSaving(true);
    const payload = {
      ...form,
      name: form.name.trim(),
      sku: form.sku.trim(),
      description: form.description.trim() || null,
      supplier: form.supplier.trim() || null,
      category: form.category.trim() || null,
    };

    try {
      if (editing) {
        await productsApi.update(editing.id, payload);
        toast.success('Producto actualizado');
      } else {
        await productsApi.create(payload);
        toast.success('Producto creado');
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo guardar el producto'));
    } finally {
      setSaving(false);
    }
  };

  const handleAdjust = async (event) => {
    event.preventDefault();
    const quantity = parseInt(adjustQty, 10);
    if (!quantity) {
      toast.error('Ingresá una cantidad distinta de cero');
      return;
    }
    try {
      await productsApi.adjustStock(adjusting.id, quantity);
      toast.success(`Stock ajustado en ${quantity > 0 ? '+' : ''}${quantity}`);
      setAdjusting(null);
      setAdjustQty('');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo ajustar el stock'));
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`¿Eliminar "${product.name}"?`)) return;
    try {
      await productsApi.remove(product.id);
      toast.success('Producto eliminado');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo eliminar el producto'));
    }
  };

  const term = search.trim().toLowerCase();
  const visible = products.filter((p) => {
    if (onlyLow && !isLow(p)) return false;
    if (!term) return true;
    return (
      p.name.toLowerCase().includes(term) ||
      p.sku.toLowerCase().includes(term) ||
      (p.category || '').toLowerCase().includes(term)
    );
  });

  const lowCount = products.filter(isLow).length;
  const inventoryValue = products.reduce((sum, p) => sum + p.cost * p.stock, 0);
  const retailValue = products.reduce((sum, p) => sum + p.price * p.stock, 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Stock" subtitle={`${products.length} producto(s) en catálogo`}>
        <Button onClick={openNew} className="rounded-sm">
          <Plus size={18} className="mr-2" />
          Nuevo producto
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Productos" value={products.length} icon={Package} />
        <StatCard
          label="Stock bajo"
          value={lowCount}
          icon={AlertTriangle}
          tone={lowCount ? 'warning' : 'default'}
        />
        <StatCard label="Valor a costo" value={formatGs(inventoryValue)} />
        <StatCard label="Valor a venta" value={formatGs(retailValue)} tone="positive" />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          placeholder="Buscar por nombre, SKU o categoría…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-sm"
        />
        <Button
          variant={onlyLow ? 'default' : 'outline'}
          onClick={() => setOnlyLow((v) => !v)}
          className="shrink-0 rounded-sm"
        >
          <AlertTriangle size={16} className="mr-2" />
          Sólo stock bajo
        </Button>
      </div>

      {loading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Package}
          title={term || onlyLow ? 'Sin resultados' : 'Todavía no hay productos'}
          description={
            term || onlyLow
              ? 'Probá cambiando los filtros.'
              : 'Cargá tu primer producto para empezar a vender.'
          }
          action={
            !term &&
            !onlyLow && (
              <Button onClick={openNew} className="rounded-sm">
                <Plus size={18} className="mr-2" />
                Nuevo producto
              </Button>
            )
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-sm border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead className="hidden sm:table-cell">SKU</TableHead>
                <TableHead className="text-right">Costo</TableHead>
                <TableHead className="text-right">Precio</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[product.category, product.supplier].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </TableCell>
                  <TableCell className="hidden font-mono text-xs sm:table-cell">
                    {product.sku}
                  </TableCell>
                  <TableCell className="text-right">
                    <Money value={product.cost} className="text-xs" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Money value={product.price} className="font-medium" />
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      type="button"
                      onClick={() => setAdjusting(product)}
                      className={`rounded-sm px-2 py-1 text-sm font-bold tabular-nums ${
                        isLow(product)
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                          : 'hover:bg-muted'
                      }`}
                      title="Ajustar stock"
                    >
                      {product.stock}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(product)} aria-label="Editar">
                        <Pencil size={15} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(product)}
                        aria-label="Eliminar"
                      >
                        <Trash2 size={15} className="text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Alta / edición */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="p-name">Nombre *</Label>
              <Input
                id="p-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="p-sku">SKU / Código *</Label>
                <Input
                  id="p-sku"
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  required
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-category">Categoría</Label>
                <Input
                  id="p-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="p-supplier">Proveedor</Label>
              <Input
                id="p-supplier"
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                placeholder="Opcional"
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="p-cost">Costo (Gs.)</Label>
                <MoneyInput
                  id="p-cost"
                  value={form.cost}
                  onChange={(cost) => setForm({ ...form, cost })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-price">Precio de venta (Gs.)</Label>
                <MoneyInput
                  id="p-price"
                  value={form.price}
                  onChange={(price) => setForm({ ...form, price })}
                  className="rounded-sm"
                />
              </div>
            </div>
            {form.price > 0 && form.cost > 0 && (
              <p className="text-xs text-muted-foreground">
                Ganancia por unidad: <strong>{formatGs(form.price - form.cost)}</strong> (
                {Math.round(((form.price - form.cost) / form.cost) * 100)}% sobre el costo)
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="p-stock">Stock actual</Label>
                <Input
                  id="p-stock"
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value, 10) || 0 })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-min">Stock mínimo</Label>
                <Input
                  id="p-min"
                  type="number"
                  min="0"
                  value={form.stock_min}
                  onChange={(e) =>
                    setForm({ ...form, stock_min: parseInt(e.target.value, 10) || 0 })
                  }
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="p-desc">Descripción</Label>
              <Textarea
                id="p-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <SubmitButton loading={saving}>{editing ? 'Guardar' : 'Crear'}</SubmitButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ajuste rápido de stock */}
      <Dialog open={Boolean(adjusting)} onOpenChange={(open) => !open && setAdjusting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar stock</DialogTitle>
          </DialogHeader>
          {adjusting && (
            <form onSubmit={handleAdjust} className="space-y-4">
              <p className="text-sm">
                <strong>{adjusting.name}</strong> — stock actual:{' '}
                <strong className="tabular-nums">{adjusting.stock}</strong>
              </p>
              <div className="space-y-2">
                <Label htmlFor="adjust">Cantidad a sumar o restar</Label>
                <Input
                  id="adjust"
                  type="number"
                  value={adjustQty}
                  onChange={(e) => setAdjustQty(e.target.value)}
                  placeholder="Ej: 10 para sumar, -3 para restar"
                  autoFocus
                  className="rounded-sm"
                />
                {adjustQty !== '' && !isNaN(parseInt(adjustQty, 10)) && (
                  <p className="text-xs text-muted-foreground">
                    Quedará en{' '}
                    <strong>{adjusting.stock + parseInt(adjustQty, 10)}</strong> unidades
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => setAdjusting(null)}
                >
                  Cancelar
                </Button>
                <SubmitButton>Ajustar</SubmitButton>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
